import React from "react";

const MedicalConditionCard = ({
  diagnosis = "Unknown",
  verification = "Unconfirmed",
  status = "Inactive",
  onsetDate = "",
  severity = "N/A",
  category = "N/A",
  isDarkTheme = true,
  icd10Code = "N/A",
  notes = [],
}) => {
  const useResponsiveStyles = () => {
    return {
      isMobile: window.innerWidth < 600,
    };
  };

  const { isMobile } = useResponsiveStyles();

  const styles = createStyles(isMobile, isDarkTheme);

  return (
    <div className="card-container" style={styles.container}>
      <div className="card-header" style={styles.header}>
        <div style={styles.titleWrapper}>
          <div style={styles.titleIcon}>
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
            </svg>
          </div>
          <h5 style={styles.title}>{diagnosis}</h5>
        </div>

        <div className="status-indicators" style={styles.statusContainer}></div>
      </div>

      <div className="card-details" style={styles.detailsContainer}>
        <div className="detail-row" style={styles.detailRow}>
          <div style={styles.detailIconWrapper}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              style={styles.detailIcon}
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
            </svg>
            <span className="detail-label" style={styles.detailLabel}>
              ICD-10 Code:
            </span>
          </div>
          <span className="detail-value" style={styles.icd10Badge}>
            {icd10Code}
          </span>
        </div>

        {notes && notes.length > 0 && (
          <div className="notes-section" style={styles.notesSection}>
            <div style={styles.notesHeader}>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                style={styles.notesIcon}
              >
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
              </svg>
              <span className="detail-label" style={styles.notesLabel}>
                Notes
              </span>
            </div>
            <div style={styles.notesListContainer}>
              {notes.map((note, index) => (
                <div key={index} style={styles.noteItem(index, notes.length)}>
                  {note}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const createStyles = (isMobile, isDarkTheme) => ({
  container: {
    backgroundColor: isDarkTheme ? "#433F81" : "#dfe0f7",
    borderRadius: "12px",
    padding: isMobile ? "12px" : "16px",
    color: isDarkTheme ? "white" : "#333",
    width: "100%",
    maxWidth: "1000px",
    boxShadow: isDarkTheme
      ? "0 8px 24px rgba(0,0,0,0.25)"
      : "0 4px 16px rgba(93, 95, 239, 0.12)",
    fontFamily: "Arial, sans-serif",
    transition: "all 0.3s ease",
  },

  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "12px",
    flexWrap: isMobile ? "wrap" : "nowrap",
    gap: "6px",
    paddingBottom: "10px",
    borderBottom: `2px solid ${isDarkTheme ? "#5A5698" : "#c9caf0"}`,
  },

  titleWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
  },

  titleIcon: {
    width: "32px",
    height: "32px",
    borderRadius: "8px",
    backgroundColor: isDarkTheme ? "#5A5698" : "#c9caf0",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: isDarkTheme ? "#B8B8D9" : "#5D5FEF",
    flexShrink: 0,
  },

  title: {
    fontSize: isMobile ? "14px" : "15px",
    fontWeight: "600",
    margin: 0,
    color: isDarkTheme ? "white" : "#333",
    letterSpacing: "-0.02em",
  },

  statusContainer: {
    display: "flex",
    gap: "8px",
    flexWrap: "wrap",
  },

  detailsContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
  },

  detailRow: {
    display: "flex",
    flexDirection: "row",
    gap: "10px",
    alignItems: "center",
  },

  detailIconWrapper: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
  },

  detailIcon: {
    color: isDarkTheme ? "#B8B8D9" : "#5D5FEF",
    flexShrink: 0,
  },

  detailLabel: {
    color: isDarkTheme ? "#B8B8D9" : "#5D5FEF",
    fontSize: "13px",
    fontWeight: "500",
  },

  icd10Badge: {
    fontSize: "13px",
    fontWeight: "600",
    color: isDarkTheme ? "white" : "#333",
    fontFamily: "monospace",
    backgroundColor: isDarkTheme ? "#5A5698" : "#c9caf0",
    padding: "4px 10px",
    borderRadius: "6px",
  },

  notesSection: {
    marginTop: "4px",
    padding: isMobile ? "10px" : "12px",
    backgroundColor: isDarkTheme ? "#5A5698" : "#c9caf0",
    borderRadius: "10px",
    borderLeft: `4px solid ${isDarkTheme ? "#8D8ED5" : "#7879c7"}`,
    boxShadow: isDarkTheme
      ? "0 2px 8px rgba(0,0,0,0.15)"
      : "0 2px 8px rgba(93, 95, 239, 0.08)",
  },

  notesHeader: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    marginBottom: "8px",
  },

  notesIcon: {
    color: isDarkTheme ? "#B8B8D9" : "#5D5FEF",
    flexShrink: 0,
  },

  notesLabel: {
    color: isDarkTheme ? "#B8B8D9" : "#5D5FEF",
    fontSize: "12px",
    fontWeight: "600",
    textTransform: "uppercase",
    letterSpacing: "0.05em",
  },

  notesListContainer: {
    display: "flex",
    flexDirection: "column",
    gap: "10px",
  },

  noteItem: (index, totalNotes) => ({
    fontSize: "13px",
    lineHeight: "1.5",
    color: isDarkTheme ? "#E8E8F5" : "#333",
    whiteSpace: "pre-wrap",
    fontWeight: "400",
    paddingBottom: index < totalNotes - 1 ? "10px" : "0",
    borderBottom:
      index < totalNotes - 1
        ? `1px solid ${isDarkTheme ? "#6B6799" : "#b5b7e6"}`
        : "none",
  }),
});

export default MedicalConditionCard;
